import React, { useState, useEffect } from 'react';
import { onPendingUsersChange, approveUser, rejectUser } from '../services/userDb';
import { useDepartments } from '../hooks/useDepartments.js';

export default function PendingRequests({ currentUser, showToast }) {
  const { departments } = useDepartments();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [deptSelections, setDeptSelections] = useState({});
  const [loadingIds, setLoadingIds] = useState({});

  useEffect(() => {
    const unsubscribe = onPendingUsersChange(users => {
      setPendingUsers(users);
      
      // Pre-fill department selections from user input
      const initialDepts = {};
      users.forEach(u => {
        if (u.department) initialDepts[u.email] = u.department;
      });
      setDeptSelections(prev => ({ ...initialDepts, ...prev }));
    });
    return unsubscribe;
  }, []);

  const handleDeptChange = (email, dept) => {
    setDeptSelections(prev => ({ ...prev, [email]: dept }));
  };

  const rowDeptOptions = (u) => {
    const s = new Set(departments);
    if (u.department) s.add(u.department);
    return [...s].sort((a, b) => a.localeCompare(b));
  };

  const handleApprove = async (user) => {
    const dept = deptSelections[user.email] || user.department;
    if (!dept) {
      showToast('Please select a department before approving', 'err');
      return;
    }
    try {
      setLoadingIds(prev => ({ ...prev, [user.email]: true }));
      await approveUser(user.email, dept, currentUser.email, user.name || "");
      showToast(`${user.email} approved successfully!`, 'ok');
    } catch (e) {
      showToast('Error approving user', 'err');
    } finally {
      if (loadingIds[user.email]) setLoadingIds(prev => ({ ...prev, [user.email]: false }));
    }
  };

  const handleReject = async (user) => {
    try {
      setLoadingIds(prev => ({ ...prev, [user.email]: true }));
      await rejectUser(user.email, currentUser.email);
      showToast(`${user.email} rejected.`, 'ok');
    } catch (e) {
      showToast('Error rejecting user', 'err');
    } finally {
      if (loadingIds[user.email]) setLoadingIds(prev => ({ ...prev, [user.email]: false }));
    }
  };

  return (
    <div className="pg">
      <div className="ph" style={{ marginBottom: '20px' }}>
        <h2>Pending Approvals</h2>
        <p style={{ color: 'var(--text2)', fontSize: '13px' }}>Manage new employee access requests</p>
      </div>
      <div className="card" style={{ padding: '0' }}>
        {pendingUsers.length === 0 ? (
          <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text2)' }}>No pending approvals</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-sec)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)' }}>NAME / EMAIL</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)' }}>STATUS</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)' }}>SELECTED DEPT</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', textAlign: 'right' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {pendingUsers.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{fontWeight: '600', fontSize: '14px'}}>{u.name || 'New User'}</div>
                    <div style={{fontSize: '12px', color: 'var(--text2)'}}>{u.email}</div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ 
                      padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                      background: u.status === 'REJECTED' ? 'var(--red-bg)' : '#fffbeb',
                      color: u.status === 'REJECTED' ? 'var(--red)' : '#d97706'
                    }}>
                      {u.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <select 
                      value={deptSelections[u.email] || ''} 
                      onChange={(e) => handleDeptChange(u.email, e.target.value)}
                      style={{ 
                        padding: '8px', fontSize: '13px', 
                        border: '1px solid var(--border)', borderRadius: '6px', 
                        width: '160px', background: u.department ? '#f0f9ff' : 'white',
                        borderColor: u.department ? '#bae6fd' : 'var(--border)'
                      }}
                      disabled={loadingIds[u.email]}
                    >
                      <option value="" disabled>Select Dept</option>
                      {rowDeptOptions(u).map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                    {u.department && <div style={{fontSize: '10px', color: '#0369a1', marginTop: '4px', fontWeight: '600'}}>Employee choice</div>}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => handleReject(u)}
                        disabled={loadingIds[u.email] || u.status === 'REJECTED'}
                        className="btn"
                        style={{ padding: '8px 16px', fontSize: '12px', background: 'white', color: 'var(--text)', border: '1px solid var(--border)', cursor: 'pointer', borderRadius: '4px', opacity: u.status === 'REJECTED' ? 0.45 : 1 }}
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        onClick={() => handleApprove(u)}
                        disabled={loadingIds[u.email] || (!deptSelections[u.email] && !u.department)}
                        className="btn btn-ok"
                        style={{ padding: '8px 16px', fontSize: '12px', cursor: 'pointer', borderRadius: '4px', background: 'var(--green)', color: 'white' }}
                      >
                        Approve Access
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
